import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { publicAxios } from "@/lib/axios";

type ShortLinkPageProps = {
  // Test seam — window.location.replace is unstubbable under jsdom.
  redirect?: (url: string) => void;
};

const ShortLinkPage: React.FC<ShortLinkPageProps> = ({
  redirect = (url) => window.location.replace(url),
}) => {
  const { t } = useTranslation();
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      if (!code) {
        setNotFound(true);
        return;
      }
      try {
        const { data } = await publicAxios.get(`/api/v1/s/${encodeURIComponent(code)}`);
        const target = String(data?.target_url || "");
        if (cancelled) return;
        // Throws on empty/garbage targets → caught below → not-found screen.
        const url = new URL(target);
        if (url.origin === window.location.origin) {
          navigate(url.pathname + url.search + url.hash, { replace: true });
        } else {
          redirect(url.toString());
        }
      } catch {
        if (!cancelled) setNotFound(true);
      }
    };
    resolve();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4 gap-4">
        <p className="text-muted-foreground">{t("shortLink.notFound")}</p>
        <Button onClick={() => navigate("/", { replace: true })}>
          {t("shortLink.goHome")}
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
    </div>
  );
};

export default ShortLinkPage;
